/**
 * Cross-component refresh signal for a campaign's "Influencer status" counts.
 *
 * `CampaignContextSidebar` (the left campaign sidebar showing Candidate /
 * Prospect / Deleted / ... counts) is a layout-level sibling of the page
 * content (see App.tsx) and fetches `getInfluencerStatuses` only once per
 * campaignId. Page-level actions like "Move to Prospect/Delete/Contacting"
 * (candidates.tsx) have no direct reference to it, so without this signal
 * the sidebar counts stay stale until a full page reload.
 *
 * Same custom-event pattern as campaignStatusStyle.ts/sidebarTriggerMode.ts,
 * used here purely as a pub/sub signal (no persisted value).
 */
import { useEffect, useState } from 'react';

export const CAMPAIGN_INFLUENCER_STATUS_CHANGED_EVENT = 'ggc:campaign-influencer-status-changed';

/** Fire after any successful influencer-status mutation on the current campaign. */
export function notifyCampaignInfluencerStatusChanged(): void {
  window.dispatchEvent(new Event(CAMPAIGN_INFLUENCER_STATUS_CHANGED_EVENT));
}

/** Returns a counter that increments whenever influencer statuses change elsewhere on the page. */
export function useCampaignInfluencerStatusRefreshSignal(): number {
  const [signal, setSignal] = useState(0);

  useEffect(() => {
    const handler = () => setSignal((s) => s + 1);
    window.addEventListener(CAMPAIGN_INFLUENCER_STATUS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CAMPAIGN_INFLUENCER_STATUS_CHANGED_EVENT, handler);
  }, []);

  return signal;
}

/**
 * Same pub/sub gap, different counts: `CampaignNavBox` (the "Candidate estimation pending" /
 * "Video status changes" nav badges) also fetches `getUncheckedAdvertiserActionCounts` only once
 * per campaignId, and `AdvertiserChanges.tsx`'s "Clear" button — on the communications/candidates
 * pages — has no direct reference to it. `clearCampaignCache()` alone only busts the IndexedDB
 * cache for the *next* fetch; it doesn't make an already-mounted `CampaignNavBox` refetch.
 */
export const CAMPAIGN_ADVERTISER_ACTION_COUNTS_CHANGED_EVENT = 'ggc:campaign-advertiser-action-counts-changed';

/** Fire after successfully clearing/checking advertiser-action notifications on the current campaign. */
export function notifyAdvertiserActionCountsChanged(): void {
  window.dispatchEvent(new Event(CAMPAIGN_ADVERTISER_ACTION_COUNTS_CHANGED_EVENT));
}

/** Returns a counter that increments whenever advertiser-action counts change elsewhere on the page. */
export function useAdvertiserActionCountsRefreshSignal(): number {
  const [signal, setSignal] = useState(0);

  useEffect(() => {
    const handler = () => setSignal((s) => s + 1);
    window.addEventListener(CAMPAIGN_ADVERTISER_ACTION_COUNTS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CAMPAIGN_ADVERTISER_ACTION_COUNTS_CHANGED_EVENT, handler);
  }, []);

  return signal;
}
